/**
 * Where a panel goes, and how it gets there.
 *
 * The one decision that matters for comfort: **panels are not head-locked**.
 * Text welded to the face is unpleasant to read, impossible to look away from,
 * and reads as a HUD — in a game whose entire aesthetic is that there is no
 * HUD. So a dock is *body*-locked instead. It sits at a fixed offset in front
 * of the player, follows them as they walk, and only rotates once the head has
 * turned further than a dead zone — and then it eases round rather than
 * snapping. Look left and the panels stay where they were; keep looking left
 * and they drift after you and settle.
 *
 * The second decision: **panels never clip through walls.** The rooms here are
 * three metres across and the player spends the game with their nose against
 * wallpaper. A dock probes ahead for solid grid cells and pulls itself in to
 * stop short, scaling as it comes so that the type keeps the same angular size
 * — a panel at 0.6 m looks exactly like the same panel at 1.25 m, it is just
 * not inside a wall.
 */

import * as THREE from 'three';
import { angleDelta, clamp } from '../../core/math';
import type { Grid } from '../../core/types';

export interface DockOptions {
  /** Metres in front of the head when there is room for it. */
  distance: number;
  /** Radians of head turn tolerated before the dock begins to follow. */
  deadZone: number;
  /** Follow damping, per second. Higher settles sooner. */
  rate: number;
  /** Metres the dock's origin sits below eye level. */
  drop: number;
  /** Half the width of the widest panel on this dock, for the wall probe. */
  halfWidth: number;
}

/**
 * Radians per second of head turn above which the dock simply stops.
 *
 * About 6°/s — slower than a deliberate look, faster than the drift of holding
 * your head still.
 */
const SETTLE_RATE = 0.1;

/** Seconds the head must be still before the dock is allowed to move again. */
const SETTLE_DELAY = 0.18;

/** Closer than this and a panel is inside the player rather than in front. */
const MIN_DISTANCE = 0.45;
/** Metres of daylight left between a panel and the wallpaper behind it. */
const WALL_MARGIN = 0.3;
/**
 * Metres between samples along a probe ray.
 *
 * The estimate is the sample that hit minus half a step, so the error is ±5 cm
 * and the real gap left in front of a wall is 25–35 cm. Three lanes at this
 * step is about forty array reads per dock per frame, which is nothing.
 */
const PROBE_STEP = 0.1;

/**
 * Metres the head may move in one frame before the dock stops easing and simply
 * goes there.
 *
 * A descent teleports the player onto a new floor. Without this the panels
 * would set off across the building at the follow rate and arrive a third of a
 * second later, having flown through several rooms on the way. Far larger than
 * anything a sprint can cover in a frame.
 */
const TELEPORT = 2;

/** Reused across every dock and every frame so placement allocates nothing. */
const _pos = new THREE.Vector3();

export class Dock {
  readonly object = new THREE.Group();

  private yaw = 0;
  private distance: number;
  /** False until the first `update`, so the dock does not ease in from origin. */
  private placed = false;
  /** Previous frame's head yaw, for the settle test. */
  private lastTarget = 0;
  /** Seconds left before the dock may move again. */
  private settling = 0;
  private lastHeadX = 0;
  private lastHeadZ = 0;

  constructor(private readonly opts: DockOptions) {
    this.distance = opts.distance;
    this.object.matrixAutoUpdate = true;
  }

  /**
   * Follow the head for one frame.
   *
   * `dt` is wall-clock rather than `ctx.dt`: a dock has to keep settling while
   * a menu is up, and the simulation's delta is zero for all of that.
   *
   * `head` is the render camera. Inside a session three has already overwritten
   * its world matrix with the headset pose (`WebXRManager.updateUserCamera`),
   * so this reads the real head whatever rig the session is built on.
   */
  update(dt: number, head: THREE.Object3D, grid: Grid | null): void {
    const m = head.matrixWorld.elements;
    const hx = m[12]!;
    const hy = m[13]!;
    const hz = m[14]!;
    // The camera's -Z column, flattened. Looking straight up or down leaves
    // nothing to flatten, and the dock simply keeps the yaw it had.
    const fx = -m[8]!;
    const fz = -m[10]!;
    const flat = Math.hypot(fx, fz);
    const yawTarget = flat > 1e-4 ? Math.atan2(-fx / flat, -fz / flat) : this.yaw;

    // A jump this large is a level change, not a walk. See `TELEPORT`.
    const jumped = Math.hypot(hx - this.lastHeadX, hz - this.lastHeadZ) > TELEPORT;
    this.lastHeadX = hx;
    this.lastHeadZ = hz;

    const fresh = !this.placed || jumped;
    if (fresh) {
      this.placed = true;
      this.yaw = yawTarget;
    } else {
      // Dead zone first, easing second. The goal is not the head's yaw but the
      // near edge of the dead zone, so a head that stops mid-turn leaves the
      // panels off to one side instead of dragging them back to centre.
      const d = angleDelta(this.yaw, yawTarget);
      const dead = this.opts.deadZone;
      const goal = Math.abs(d) > dead ? yawTarget - Math.sign(d) * dead : this.yaw;

      // Settle before moving. A panel that eases after the head *while the head
      // is turning* puts a large object into the periphery moving on its own
      // account, which is the classic recipe for sickness and is exactly what
      // got reported: "the menu momentum slide is a little nauseating".
      //
      // So the dock holds absolutely still while the head is in motion and
      // catches up once it stops. Nothing ever slides across the view while
      // there is head motion to disagree with it. It matters most on a headset
      // with no artificial turn, where looking around *is* the only way to
      // turn and the head is therefore moving most of the time.
      const headRate = dt > 0 ? Math.abs(angleDelta(this.lastTarget, yawTarget)) / dt : 0;
      this.lastTarget = yawTarget;
      if (headRate > SETTLE_RATE) {
        this.settling = SETTLE_DELAY;
      } else if (this.settling > 0) {
        this.settling = Math.max(0, this.settling - dt);
      }
      if (this.settling <= 0) {
        const k = 1 - Math.exp(-this.opts.rate * dt);
        this.yaw += angleDelta(this.yaw, goal) * k;
      }
    }

    const dfx = -Math.sin(this.yaw);
    const dfz = -Math.cos(this.yaw);
    const want = grid ? this.clearance(grid, hx, hz, dfx, dfz) : this.opts.distance;
    // Eased, so walking into a doorway does not snap the panels forward.
    this.distance = fresh ? want : this.distance + (want - this.distance) * (1 - Math.exp(-8 * dt));

    _pos.set(hx + dfx * this.distance, hy - this.opts.drop, hz + dfz * this.distance);
    // Position follows the body almost immediately — only fast enough to take
    // the jitter off a tracked head. It is the *yaw* that is lazy, above; a
    // dock that lagged in position would drift off the shoulder while walking.
    this.object.position.lerp(_pos, fresh ? 1 : 1 - Math.exp(-14 * dt));
    this.object.rotation.set(0, this.yaw, 0, 'YXZ');
    // Constant angular size: a dock that had to come in close would otherwise
    // present the same text twice as large, which is the wrong half of the fix.
    this.object.scale.setScalar(this.distance / this.opts.distance);
  }

  /**
   * Put the dock exactly where it belongs right now, with no easing.
   *
   * Used when a menu opens: a pause screen that slides into place from
   * wherever the last one was left is a distraction at the moment the player
   * most wants to read something.
   */
  snap(head: THREE.Object3D, grid: Grid | null): void {
    this.placed = false;
    this.update(1, head, grid);
  }

  /**
   * How far ahead there is room, in metres.
   *
   * Three parallel rays — centre and both panel edges — stepped through the
   * occupancy grid. Grid only: `isSolidAt` is a single array read and covers
   * every wall in the building, which is what panels actually collide with.
   * Props are ignored on purpose; almost all of them are below the dock's
   * height, and a panel that flinched away from a chair would be worse than one
   * that occasionally overlaps a bookshelf.
   */
  private clearance(grid: Grid, hx: number, hz: number, fx: number, fz: number): number {
    const max = this.opts.distance;
    // Deliberately the *unscaled* half-width, not the one the dock is currently
    // drawn at. Probing at the current scale would be a feedback loop — pulling
    // in narrows the probe, which finds more room, which pushes back out.
    const half = this.opts.halfWidth;
    // Perpendicular to the facing, in the XZ plane.
    const px = -fz * half;
    const pz = fx * half;
    let nearest = max;
    for (let lane = -1; lane <= 1; lane++) {
      const ox = px * lane;
      const oz = pz * lane;
      for (let d = PROBE_STEP; d <= max + WALL_MARGIN; d += PROBE_STEP) {
        if (!grid.isSolidAt(hx + ox + fx * d, hz + oz + fz * d)) continue;
        // The surface is somewhere in the step just crossed; split it.
        nearest = Math.min(nearest, d - PROBE_STEP * 0.5 - WALL_MARGIN);
        break;
      }
    }
    return clamp(nearest, MIN_DISTANCE, max);
  }

  dispose(): void {
    this.object.removeFromParent();
  }
}

/**
 * The grip space of one hand, if there is one and somebody has put it in the
 * scene.
 *
 * `layer.ts` hangs the map off the player's left hand when this returns
 * something — a map you raise to look at, which is both more pleasant than a
 * floating sheet and how anybody actually uses a map. The `parent` check is the
 * whole safety story: three updates a controller's *local* matrix every XR
 * frame whether or not it is in the scene graph, so a grip nobody parented has
 * a world matrix that means nothing, and hanging a panel off it would leave the
 * map sitting at the origin of the building. When that is the case the caller
 * falls back to docking the map like any other panel.
 *
 * Deliberately does not add the grip to anything itself. Whoever owns the XR
 * session owns the rig, and re-parenting their controller out from under them
 * would move the player's hands.
 */
export function handGrip(renderer: THREE.WebGLRenderer, handedness: XRHandedness): THREE.Object3D | null {
  const session = renderer.xr.getSession();
  if (!session) return null;
  const sources = session.inputSources;
  for (let i = 0; i < sources.length; i++) {
    if (sources[i]?.handedness !== handedness) continue;
    // three indexes its controllers by position in `session.inputSources`.
    const grip = renderer.xr.getControllerGrip(i);
    return grip.parent ? grip : null;
  }
  return null;
}

